import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Conversation } from '../../../models/Conversation';
import { Dropdown } from 'primeng/dropdown';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../state/reducers';
import {
  BlockSenderClose,
  BlockSenderOpen,
  ExportConversationsAsPdfs,
  UpdateConversationReadStatus,
  MarkConversationsAsUnread,
  MarkManyConversationsAsResolved
} from '../../../conversation/state/actions';
import { getIsBlockSenderOpen } from '../../../conversation/state/selectors';
import { takeWhile } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { isEmbedMode } from '../../../state/selectors';
import { TranslateService } from '@ngx-translate/core';

interface BatchAction {
  slug: string;
  label: string;
  disabled: boolean;
}

@Component({
  selector: 'conversation-bulk-actions',
  templateUrl: './conversation-bulk-actions.component.html',
  styleUrls: ['./conversation-bulk-actions.component.scss']
})
export class ConversationBulkActionsComponent implements OnInit, OnDestroy {
  @ViewChild('dropdown') dropdown?: Dropdown;
  @Output() actionFired = new EventEmitter();
  @Input() selectedConversations: Conversation[] = [];
  alive = true;
  batchActions: BatchAction[] = [];
  selectedAction: BatchAction | null = null;
  blockSenderOpen = false;
  embedMode$: Observable<boolean>;
  embedMode = false;
  translations = {
    markRead: 'Mark as read',
    markUnread: 'Mark as unread',
    exportPdf: 'Export as PDF',
    blockSender: 'Block sender',
    resolveConversation: 'Resolve conversation',
    resolveAndExport: 'Resolve and export as PDF'
  };

  constructor(private store: Store<AppState>, private translateSerivce: TranslateService) {
    this.embedMode$ = this.store.pipe(select(isEmbedMode)).pipe(takeWhile(() => this.alive));
    this.embedMode$.subscribe(embedMode => {
      this.embedMode = embedMode;
      this.setBatchActions();
    })
  }

  ngOnInit(): void {
    this.subscribeToBlockSenderOpen();
    this.translateSerivce.get('ui.conversation_bulk_actions.batch_actions').pipe(takeWhile(_ => this.alive)).subscribe((trs) => {
      this.translations = {
        markRead: trs['mark_read'],
        markUnread: trs['mark_unread'],
        exportPdf: trs['export_pdf'],
        blockSender: trs['block_sender'],
        resolveConversation: trs['resolve_conversation'],
        resolveAndExport: trs['resolve_and_export']
      };
      this.setBatchActions();
    })
  }

  ngOnDestroy(): void {
    this.alive = false;
  }

  setBatchActions(): void {
    if (this.embedMode) {
      this.batchActions = [
        { slug: 'mark-read', label: this.translations.markRead, disabled: false },
        { slug: 'mark-unread', label: this.translations.markUnread, disabled: false },
        { slug: 'block', label: this.translations.blockSender, disabled: false },
        { slug: 'resolve', label: this.translations.resolveConversation, disabled: false },
      ];
    } else {
      this.batchActions = [
        { slug: 'mark-read', label: this.translations.markRead, disabled: false },
        { slug: 'mark-unread', label: this.translations.markUnread, disabled: false },
        { slug: 'export', label: this.translations.exportPdf, disabled: false },
        { slug: 'block', label: this.translations.blockSender, disabled: false },
        { slug: 'resolve', label: this.translations.resolveConversation, disabled: false },
        { slug: 'resolve-and-export', label: this.translations.resolveAndExport, disabled: false },
      ];
    }
  }

  handleAction(action?: BatchAction): void {
    if (action) {
      switch (action.slug) {
        case 'mark-read':
          this.store.dispatch(UpdateConversationReadStatus({ conversations: this.selectedConversations }));
          break;
        case 'mark-unread':
          this.store.dispatch(MarkConversationsAsUnread({ conversations: this.selectedConversations }));
          break;
        case 'export':
          this.store.dispatch(ExportConversationsAsPdfs({ conversations: this.selectedConversations }));
          break;
        case 'block':
          this.store.dispatch(BlockSenderOpen());
          break;
        case 'resolve':
          this.store.dispatch(MarkManyConversationsAsResolved({
            conversations: this.checkIfConversationIsResolved(this.selectedConversations),
            saveToPms: false,
            downloadPdf: false,
            sendTextToPms: false
          }));
          break;
        case 'resolve-and-export':
          this.store.dispatch(MarkManyConversationsAsResolved({
            conversations: this.checkIfConversationIsResolved(this.selectedConversations),
            saveToPms: false,
            downloadPdf: true,
            sendTextToPms: false
          }));
          return;
      }

      this.handleActionFired();
    }
  }

  closeBlockSender(): void {
    this.store.dispatch(BlockSenderClose());
    this.handleActionFired();
  }

  checkIfConversationIsResolved(conversations: Conversation[]): Conversation[] {
    return conversations.filter(conversation => !conversation.resolvedAt);
  }

  subscribeToBlockSenderOpen(): void {
    this.store
      .pipe(select(getIsBlockSenderOpen))
      .pipe(takeWhile(() => this.alive))
      .subscribe((isOpen) => {
        this.blockSenderOpen = isOpen;

        if (!isOpen) {
          this.handleActionFired();
        }
      });
  }

  handleActionFired(): void {
    this.actionFired.emit(); 
    setTimeout(() => {
      if (this.dropdown) {
        this.dropdown.clear();
      }
    }, 0);
  }
}
